import { faChevronDown, faChevronRight, faFolderBlank } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isDescendantInCache } from '../../helpers';

const TREE_PER_PAGE = 10;

const MoveFolderOption = memo(({
  folder,
  level,
  selectedParentId,
  onSelect,
  movingItemId,
  getItemsByParentId,
  itemsCache,
  itemsTotals,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const isDisabled = movingItemId
    && (folder.id === movingItemId || isDescendantInCache(movingItemId, folder.id, itemsCache));
  const isSelected = selectedParentId === folder.id;

  useEffect(() => {
    if (isExpanded && !itemsCache[folder.id]) {
      const controller = new AbortController();
      getItemsByParentId(folder.id, controller, 1, TREE_PER_PAGE);
      return () => controller.abort();
    }
    return undefined;
  }, [isExpanded, folder.id, getItemsByParentId, itemsCache]);

  const allItems = itemsCache[folder.id] || [];
  const children = allItems.filter((item) => item.type === 'folder');
  const isLoaded = folder.id in (itemsCache || {});
  const hasMore = allItems.length < (itemsTotals?.[folder.id] || 0);

  const handleLoadMore = async (e) => {
    e.stopPropagation();
    setLoadingMore(true);
    const controller = new AbortController();
    const nextPage = Math.floor(allItems.length / TREE_PER_PAGE) + 1;
    await getItemsByParentId(folder.id, controller, nextPage, TREE_PER_PAGE);
    setLoadingMore(false);
  };

  return (
    <li className="move-folder-picker__item">
      <div
        className={`move-folder-picker__row ${isSelected ? 'move-folder-picker__row--selected' : ''} ${isDisabled ? 'move-folder-picker__row--disabled' : ''}`}
        style={{ paddingLeft: `${level * 1}rem` }}
        onClick={() => {
          if (!isDisabled) {
            onSelect(folder.id);
          }
        }}
      >
        <button
          type="button"
          className="move-folder-picker__toggle"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
          }}
        >
          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
        </button>
        <FontAwesomeIcon icon={faFolderBlank} className="move-folder-picker__icon" />
        <span>{folder.name}</span>
      </div>
      {isExpanded && (
        <ul className="move-folder-picker__children">
          {isLoaded && children.length === 0 && (
            <li className="move-folder-picker__empty">{t('no-items')}</li>
          )}
          {children.map((child) => (
            <MoveFolderOption
              key={child.id}
              folder={child}
              level={level + 1}
              selectedParentId={selectedParentId}
              onSelect={onSelect}
              movingItemId={movingItemId}
              getItemsByParentId={getItemsByParentId}
              itemsCache={itemsCache}
              itemsTotals={itemsTotals}
            />
          ))}
          {hasMore && (
            <li className="move-folder-picker__show-more" style={{ paddingLeft: `${(level + 1) * 1}rem` }}>
              <button type="button" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? '...' : t('show-more')}
              </button>
            </li>
          )}
        </ul>
      )}
    </li>
  );
});

const MoveFolderPicker = ({
  selectedParentId,
  onSelect,
  movingItemId,
  currentParentId,
  getItemsByParentId,
  itemsCache,
  itemsTotals,
}) => {
  const { t } = useTranslation();
  const [loadingMoreRoot, setLoadingMoreRoot] = useState(false);
  const allRootItems = itemsCache.root || [];
  const rootFolders = allRootItems.filter((item) => item.type === 'folder');
  const hasMoreRoot = allRootItems.length < (itemsTotals?.root || 0);
  const isParentSelected = selectedParentId === null;
  const isParentDisabled = currentParentId == null;

  const handleLoadMoreRoot = async (e) => {
    e.stopPropagation();
    setLoadingMoreRoot(true);
    const controller = new AbortController();
    const nextPage = Math.floor(allRootItems.length / TREE_PER_PAGE) + 1;
    await getItemsByParentId(null, controller, nextPage, TREE_PER_PAGE);
    setLoadingMoreRoot(false);
  };

  return (
    <ul className="move-folder-picker">
      <li>
        <div
          className={`move-folder-picker__row move-folder-picker__row--parent ${isParentSelected ? 'move-folder-picker__row--selected' : ''} ${isParentDisabled ? 'move-folder-picker__row--disabled' : ''}`}
          onClick={() => {
            if (!isParentDisabled) {
              onSelect(null);
            }
          }}
        >
          <FontAwesomeIcon icon={faFolderBlank} className="move-folder-picker__icon" />
          <span>{t('reports-parent')}</span>
        </div>
      </li>
      {rootFolders.map((folder) => (
        <MoveFolderOption
          key={folder.id}
          folder={folder}
          level={0}
          selectedParentId={selectedParentId}
          onSelect={onSelect}
          movingItemId={movingItemId}
          getItemsByParentId={getItemsByParentId}
          itemsCache={itemsCache}
          itemsTotals={itemsTotals}
        />
      ))}
      {hasMoreRoot && (
        <li className="move-folder-picker__show-more">
          <button type="button" onClick={handleLoadMoreRoot} disabled={loadingMoreRoot}>
            {loadingMoreRoot ? '...' : t('show-more')}
          </button>
        </li>
      )}
    </ul>
  );
};

export default MoveFolderPicker;
